import { ElementRef, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ContentTypes, LsqValidators, StringI18nValidator } from '@lsq-frontend/common-ui';
import {
    AccountRequestAddress,
    BankAccount,
    BankAccountType,
    GeneratePresignedUrlForPaymentMethodService,
    GeneratePresignedUrlRequest,
    GetPrimaryCompanyAddressForSellerRequest,
    GetPrimaryCompanyAddressForSellerService,
    PrimaryAddress,
    UploadType
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject, combineLatest, merge, Observable, Subject, Subscription } from 'rxjs';
import { FileUpload, HttpMethod, Uploader } from 'rxjs-uploader';
import { first, map, shareReplay, startWith } from 'rxjs/operators';
import {
    BANK_VERIFICATION_FILE_SIZE_LIMIT,
    SupportedBankCountry,
    SupportedBankCountryCodes,
    SupportedBankTransferType
} from '../../../constants/bank.constants';
import { BankAccountsService } from './bank-accounts.service';
import { ExtendedAddress } from '@lsq-frontend/remote-services/enterprise/common';
import { SupportedBankPaymentMethod } from './bank-accounts.models';

@Injectable({ providedIn: 'root' })
export class AddIntlBankAccountFormService {
    private _init$ = new Subject<void>();

    private _fileSizeLimitInMb = BANK_VERIFICATION_FILE_SIZE_LIMIT;

    private _accountNumbersDoNotMatchCopy = this._i18n({
        id: 'addBankAccountNumbersDoNotMatch',
        value: `Account numbers do not match.`
    });
    private readonly uploadWrongFileTypeErrorMessageI18N = this._i18n(
        'Your file upload must be in PDF, PNG, JPEG, or TIFF format.'
    );
    private readonly contentSizeErrorMessageI18N = this._i18n(
        'Your file exceeds the {{limit}}MB limit.',
        { limit: this._fileSizeLimitInMb }
    );
    private readonly genericErrorMessageI18N = this._i18n(
        'The upload was not successful, please try again and if the problem persists contact support at <a href="mailto:fasttrack-support@lsq.com" class="mail-to preserve-case">fasttrack-support@lsq.com</a>'
    );

    private _formGroupSubject = new BehaviorSubject<FormGroup>(null);
    private _primaryCompanyAddress: PrimaryAddress | ExtendedAddress;
    private _companyName: string;
    private _beneficiaryAddressSubscription: Subscription;

    public formGroup$ = this._formGroupSubject.asObservable();

    public fileUpload$: Observable<FileUpload>;
    public uploadFileSubject = new Subject();

    private _fileUploadSuccessfulSubject = new Subject<undefined>();
    public fileUploadSuccessful$ = this._fileUploadSuccessfulSubject.asObservable();

    private _fileUploadErrorSubject = new Subject<Error>();
    public fileUploadError$ = this._fileUploadErrorSubject.asObservable();

    private _hasFileSelectedSubject = new BehaviorSubject<boolean>(false);
    public hasFileSelected$ = this._hasFileSelectedSubject.asObservable();

    private _uploadErrorMessageSubject = new BehaviorSubject<string>(null);
    public uploadErrorMessage$ = this._uploadErrorMessageSubject.asObservable();

    private _isSubmittingSubject = new BehaviorSubject<boolean>(false);
    public isSubmitting$ = this._isSubmittingSubject.asObservable();
    public disableSubmit$: Observable<boolean>;

    public fileUploader: Uploader = new Uploader<FileUpload>({
        allowedContentTypes: ContentTypes.ImagesAndPdfs,
        fileSizeLimitMb: this._fileSizeLimitInMb,
        fileCountLimit: 1,
        requestUrl: '',
        uploadFileAsBody: true,
        disallowedContentTypeErrorMessage: () => this.uploadWrongFileTypeErrorMessageI18N,
        disallowedContentSizeErrorMessage: () => this.contentSizeErrorMessageI18N,
        allFilesQueuedCallback: async (fileUploads) => {
            // This waits for the form to be submitted.
            await this.uploadFileSubject.pipe(first()).toPromise();

            this.fileUploader.patchRequestOptions({
                method: HttpMethod.Put
            });
            const presignedUrl = await this._getPresignedUrl(fileUploads[0].file.type);
            this.fileUploader.setRequestUrl(presignedUrl);
            return fileUploads;
        },
        allFilesUploadedCallback: () => {
            this._fileUploadSuccessfulSubject.next();
        }
    });

    constructor(
        private _i18n: I18n,
        private _stringI18nValidator: StringI18nValidator,
        private _generatePresignedUrlService: GeneratePresignedUrlForPaymentMethodService,
        private _bankAccountsService: BankAccountsService,
        private _getPrimaryCompanyAddressForSellerService: GetPrimaryCompanyAddressForSellerService
    ) {
        this.fileUploader.errorStream.subscribe((error) => {
            if (
                error.message === this.uploadWrongFileTypeErrorMessageI18N ||
                error.message === this.contentSizeErrorMessageI18N
            ) {
                this._uploadErrorMessageSubject.next(error.message);
            } else {
                this._uploadErrorMessageSubject.next(this.genericErrorMessageI18N);
            }
        });
    }

    public resetFileSelected(): void {
        this._hasFileSelectedSubject.next(false);
    }

    public initFormGroup(): FormGroup {
        this._init$.next();
        const formGroup = this.createBankingInfoFormGroup(this._accountNumbersDoNotMatchCopy);
        this._formGroupSubject.next(formGroup);

        this.disableSubmit$ = combineLatest([
            formGroup.statusChanges,
            this.hasFileSelected$,
            this.uploadErrorMessage$
        ]).pipe(
            map(
                ([formValidity, hasFileSelected, uploadErrorMessage]) =>
                    formValidity !== 'VALID' || !hasFileSelected || !!uploadErrorMessage
            ),
            startWith(true)
        );

        return formGroup;
    }

    public async submitForm(
        isEnrolling = false,
        bankPaymentMethod: SupportedBankPaymentMethod,
        options?: { sellerKey: string; companyKey: string }
    ): Promise<void> {
        const formGroup = await this.formGroup$.pipe(first()).toPromise();
        formGroup.markAllAsTouched();

        const selectedCountry: string = SupportedBankCountry[bankPaymentMethod.country];

        if (formGroup.valid) {
            this._isSubmittingSubject.next(true);

            let bankAccountRoutingNumber: string;
            if (
                bankPaymentMethod.country === SupportedBankCountry.UNITED_STATES &&
                bankPaymentMethod.transferType === SupportedBankTransferType.WIRE
            ) {
                bankAccountRoutingNumber = formGroup.get('routingNumber').value;
            }
            const bankAccount = new BankAccount({
                accountName: formGroup.get('accountName').value,
                accountNumber: formGroup.get('accountNumber').value,
                routingNumber: bankAccountRoutingNumber,
                bankName: formGroup.get('bankName').value,
                bankAccountType: BankAccountType.WIRE,
                active: true
            });

            let swiftCode: string;
            if (
                bankPaymentMethod.transferType === SupportedBankTransferType.WIRE &&
                bankPaymentMethod.country !== SupportedBankCountry.UNITED_STATES
            ) {
                swiftCode = formGroup.get('swiftCode').value;
            } else {
                swiftCode = ' ';
            }

            let routingNumber: string;
            if (
                bankPaymentMethod.country === SupportedBankCountry.CHINA ||
                bankPaymentMethod.country === SupportedBankCountry.INDIA ||
                bankPaymentMethod.country === SupportedBankCountry.CANADA
            ) {
                routingNumber = formGroup.get('routingNumber').value;
            }
            const bankAddress = new AccountRequestAddress({
                city: formGroup.get('city').value,
                state: formGroup.get('state').value,
                postalCode: formGroup.get('postal').value,
                streetLine1: formGroup.get('address').value,
                streetLine2: formGroup.get('address2').value,
                countryCode: SupportedBankCountryCodes[selectedCountry]
            });

            const beneAddress = new AccountRequestAddress({
                name: formGroup.get('beneName').value,
                city: formGroup.get('beneCity').value,
                state: formGroup.get('beneState').value,
                postalCode: formGroup.get('benePostal').value,
                streetLine1: formGroup.get('beneAddress').value,
                streetLine2: formGroup.get('beneAddress2').value,
                countryCode: SupportedBankCountryCodes[selectedCountry]
            });

            this.uploadFileSubject.next();
            const errorOrUndefined = await merge(this.fileUploadSuccessful$, this.fileUploadError$)
                .pipe(first())
                .toPromise();
            if (errorOrUndefined) {
                this._isSubmittingSubject.next(false);
                throw errorOrUndefined;
            }
            try {
                await this._bankAccountsService.createIntlBankAccountAndSubmitForVerification(
                    bankAccount,
                    swiftCode,
                    routingNumber,
                    bankAddress,
                    SupportedBankCountryCodes[selectedCountry],
                    formGroup.get('useAddress').value,
                    beneAddress,
                    isEnrolling,
                    options
                );

                this._isSubmittingSubject.next(false);
            } catch (error) {
                this._isSubmittingSubject.next(false);
                throw error;
            }
        }
    }

    public createBankingInfoFormGroup(accountNumbersDoNotMatchErrorCopy: string): FormGroup {
        const formGroup = new FormGroup({
            accountName: new FormControl('', [Validators.required]),
            accountNumber: new FormControl('', [Validators.required]),
            verifyAccountNumber: new FormControl('', [Validators.required]),
            useAddress: new FormControl(true, []),
            beneName: new FormControl(
                {
                    value: this._companyName || '',
                    disabled: true
                },
                [Validators.required]
            ),
            beneAddress: new FormControl(
                {
                    value: this._primaryCompanyAddress?.streetLine1 || '',
                    disabled: true
                },
                [Validators.required, LsqValidators.maxLength(35)]
            ),
            beneAddress2: new FormControl(
                {
                    value: this._primaryCompanyAddress?.streetLine2 || '',
                    disabled: true
                },
                [LsqValidators.maxLength(35)]
            ),
            beneCity: new FormControl(
                {
                    value: this._primaryCompanyAddress?.city || '',
                    disabled: true
                },
                [Validators.required]
            ),
            beneState: new FormControl(
                {
                    value: this._primaryCompanyAddress?.state || '',
                    disabled: true
                },
                []
            ),
            benePostal: new FormControl({
                value: this._primaryCompanyAddress?.postalCode || '',
                disabled: true
            }),
            swiftCode: new FormControl('', [
                LsqValidators.minLength(8),
                LsqValidators.maxLength(11)
            ]),
            routingNumber: new FormControl('', []),
            bankName: new FormControl('', [Validators.required]),
            address: new FormControl('', [Validators.required, LsqValidators.maxLength(35)]),
            address2: new FormControl('', [LsqValidators.maxLength(35)]),
            city: new FormControl('', [Validators.required]),
            state: new FormControl('', []),
            postal: new FormControl('', [])
        });
        const accountNumberControl = formGroup.get('accountNumber');
        accountNumberControl.addValidators(
            this._stringI18nValidator.matchValueSource(
                () => formGroup?.get('verifyAccountNumber'),
                accountNumbersDoNotMatchErrorCopy
            )
        );
        const verifyAccountNumberControl = formGroup.get('verifyAccountNumber');
        verifyAccountNumberControl.addValidators(
            this._stringI18nValidator.matchValueConfirm(
                () => formGroup?.get('accountNumber'),
                accountNumbersDoNotMatchErrorCopy
            )
        );

        this._beneficiaryAddressSubscription?.unsubscribe();
        this._beneficiaryAddressSubscription = formGroup.controls[
            'useAddress'
        ].valueChanges.subscribe((value) => {
            if (value) {
                formGroup.controls['beneName'].setValue(this._companyName || '');
                formGroup.controls['beneAddress'].setValue(
                    this._primaryCompanyAddress?.streetLine1 || ''
                );
                formGroup.controls['beneAddress2'].setValue(
                    this._primaryCompanyAddress?.streetLine2 || ''
                );
                formGroup.controls['beneCity'].setValue(this._primaryCompanyAddress?.city || '');
                formGroup.controls['beneState'].setValue(this._primaryCompanyAddress?.state || '');
                formGroup.controls['beneCity'].setValue(this._primaryCompanyAddress?.city || '');
                formGroup.controls['beneState'].setValue(this._primaryCompanyAddress?.state || '');
                formGroup.controls['benePostal'].setValue(
                    this._primaryCompanyAddress?.postalCode || ''
                );

                formGroup.controls['beneName'].disable();
                formGroup.controls['beneAddress'].disable();
                formGroup.controls['beneAddress2'].disable();
                formGroup.controls['beneCity'].disable();
                formGroup.controls['beneState'].disable();
                formGroup.controls['benePostal'].disable();
            } else {
                formGroup.controls['beneName'].enable();
                formGroup.controls['beneAddress'].enable();
                formGroup.controls['beneAddress2'].enable();
                formGroup.controls['beneCity'].enable();
                formGroup.controls['beneState'].enable();
                formGroup.controls['benePostal'].enable();
            }
        });

        return formGroup;
    }

    public setFileSource(fileSourceElement: ElementRef): void {
        this.fileUpload$ = this.fileUploader
            .streamFileUploads(fileSourceElement.nativeElement as HTMLElement)
            .pipe(
                map(([fileUpload]) => fileUpload),
                shareReplay(1)
            );
    }

    public handleFileSelected(fileRef: string): void {
        this._hasFileSelectedSubject.next(!!fileRef);
        this._uploadErrorMessageSubject.next(null);
    }

    public async loadCompanyInfo(companyInformation?: {
        companyAddress: PrimaryAddress | ExtendedAddress;
        companyName: string;
    }): Promise<void> {
        if (companyInformation?.companyAddress && companyInformation?.companyName) {
            this._primaryCompanyAddress = companyInformation?.companyAddress;
            this._companyName = companyInformation?.companyName;
        } else {
            const response = await this._getPrimaryCompanyAddressForSellerService.executeOnce(
                new GetPrimaryCompanyAddressForSellerRequest()
            );
            this._primaryCompanyAddress = response?.primaryAddress?.address;
            this._companyName = response?.primaryAddress?.companyName;
        }
    }

    private async _getPresignedUrl(fileExtension: string): Promise<string> {
        fileExtension = fileExtension.slice(fileExtension.indexOf('/') + 1);
        try {
            const request = new GeneratePresignedUrlRequest({
                uploadType: UploadType.PAYMENT_VERIFICATION,
                fileExtension: fileExtension
            });
            return (await this._generatePresignedUrlService.executeOnce(request)).presignedUrl;
        } catch (error) {
            this._fileUploadErrorSubject.next(error);
            throw error;
        }
    }
}
